import React from 'react';
import PropTypes from 'prop-types';

import {
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
}                                       from '@material-ui/core';

import { makeStyles, useTheme }         from '@material-ui/core/styles';

import { PieChart }                     from 'react-minimal-pie-chart';

import { 
  fmtBigInteger,
  fmtBytes,
}                                       from '../../utils/formatters';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: 10,
  },
  table: {
    minWidth: 250,
    },
  cell: {
    padding: 0,
  },
  rejected: {
    backgroundColor: theme.palette.primary.main,
  },
  skipped: {
    backgroundColor: theme.palette.warning.main,
  },
  failed: {
    backgroundColor: theme.palette.error.main,
  },
  succeeded: {
    backgroundColor: theme.palette.success.main,
  },
}));

function ExecutionResultTableRow(props) {
  const { name, count, total, size, color, variant, onClickCount } = props;
  const classes = useStyles();

  const percent = total?Math.round((count/total)*100):null;

  let _name=name;
  let _count=count;
  let _percent=percent;
  let _size=size;
  let cellVariant='inherit';
  
  if(variant==='heading') {
    _name='Objects';
    _count='Count';
    _percent='%';
    _size='Size';
    cellVariant='caption';
  }
  
  if(!_count) {
    return(null);
  } 

  let _size_parts=fmtBytes(_size,1).split(' ');

  return(
    <TableRow>
      <TableCell className={classes.cell}>
        {color?<React.Fragment><span style={{ backgroundColor: color }}>&nbsp;</span>&nbsp;</React.Fragment>:null}
        <Typography variant='caption' style={{ fontStyle: color?'italic':'normal'}}>{_name}</Typography>
      </TableCell>
      <TableCell className={classes.cell} align="right">
        {onClickCount?
          <Link onClick={onClickCount}>
            {fmtBigInteger(_count)}
          </Link>
          :
          <Typography variant={cellVariant}>{fmtBigInteger(_count)}</Typography>
        }
      </TableCell>
      <TableCell className={classes.cell} align="right"><Typography variant={cellVariant}>{_percent}</Typography></TableCell>
      {variant==='heading'?
      <TableCell className={classes.cell} align="center" colSpan={2}><Typography variant={cellVariant}>{_size}</Typography></TableCell>
      :
      <React.Fragment>
        <TableCell className={classes.cell} align="right"><Typography variant={cellVariant}>{_size_parts[0]}</Typography></TableCell>
        <TableCell className={classes.cell} align="left" style={{paddingLeft: '4px'}}><Typography variant={cellVariant}>{_size_parts[1]}</Typography></TableCell>
      </React.Fragment>
      }
    </TableRow>
  );
  
}
ExecutionResultTableRow.propTypes = {
  name: PropTypes.string,
  count: PropTypes.any,
  percent: PropTypes.any,
  size: PropTypes.any,
  color: PropTypes.any,
  variant: PropTypes.oneOf(['heading', 'row']),
  onClickCount: PropTypes.func
};

ExecutionResultTableRow.defaultProps = {
  variant: 'row'
};

export default function ExecutionResults(props) {
    
  const { value, variant, onSelectResultType } = props;
  
  const classes = useStyles();
  const theme = useTheme();

  const handleSelectResultType = (type) => {
    if(onSelectResultType) {
      onSelectResultType(type);
    }
  };

  const rejectedColor=theme.palette.primary.main;
  const skippedColor=theme.palette.warning.main;
  const failedColor=theme.palette.error.main;
  const succeededColor=theme.palette.success.main;
  const noColor=theme.palette.background.paper;
  
  const selected=value.Inputs.Selected.Objects;
  const selectedBytes=value.Inputs.Selected.Bytes;
  const rejected=value.Inputs.Rejected.Objects;
  const rejectedBytes=value.Inputs.Rejected.Bytes;
  const skipped=value.Inputs.Skipped.Objects;
  const skippedBytes=value.Inputs.Skipped.Bytes;
  const failed=value.Inputs.Failed.Objects;
  const failedBytes=value.Inputs.Failed.Bytes;
  const succeeded=value.Inputs.Succeeded.Objects;
  const succeededBytes=value.Inputs.Succeeded.Bytes;
  const completed=rejected+skipped+failed+succeeded;
  const completedBytes=rejectedBytes+skippedBytes+failedBytes+succeededBytes;
  const remaining=selected-completed;
  const remainingBytes=selectedBytes-completedBytes;
  const retried=value.Inputs.Retried.Objects;
  const retriedBytes=value.Inputs.Retried.Bytes;

  const artifacts=value.Outputs.Artifacts.Objects || 0;
  const artifactBytes=value.Outputs.Artifacts.Bytes || 0;
  const datas=value.Outputs.Data.Objects || 0;
  const dataBytes=value.Outputs.Data.Bytes || 0;
  const diagnostics=value.Outputs.Diagnostics.Objects || 0;
  const diagnosticBytes=value.Outputs.Diagnostics.Bytes || 0;
  const samples=value.Outputs.Samples.Objects || 0;
  const sampleBytes=value.Outputs.Samples.Bytes || 0;
  
  const outputs=artifacts+datas+diagnostics+samples;
  const outputBytes=artifactBytes+dataBytes+diagnosticBytes+sampleBytes;
  const outputTypes=Math.max(artifacts,1)+Math.max(datas,1)+Math.max(diagnostics,1)+Math.max(samples,1);

  const percentComplete=Math.round((completed/selected)*100);
  
  const resultData=[
          { title: 'Rejected', value: rejected, color: rejectedColor },
          { title: 'Skipped', value: skipped, color: skippedColor },
          { title: 'Failed', value: failed, color: failedColor },
          { title: 'Succeeded', value: succeeded, color: succeededColor },
          { title: 'Remaining', value: remaining, color: '#EEE8' },
        ].filter((item) => item.value>0);
  
  if (variant==='table') {
    return (
      <TableContainer className={classes.root}>
        <Table className={classes.table} size="small">
          <TableHead>
            <ExecutionResultTableRow variant='heading'/>
          </TableHead>
          <TableBody>
            <ExecutionResultTableRow name='Inputs' count={selected} size={selectedBytes}/>
            <ExecutionResultTableRow name='Rejected' count={rejected} total={selected} size={rejectedBytes} color={rejectedColor} onClickCount={() => handleSelectResultType('REJECTED')}/>
            <ExecutionResultTableRow name='Skipped' count={skipped} total={selected} size={skippedBytes} color={skippedColor} onClickCount={() => handleSelectResultType('SKIPPED')}/>
            <ExecutionResultTableRow name='Failed' count={failed} total={selected} size={failedBytes} color={failedColor} onClickCount={() => handleSelectResultType('FAILED')}/>
            <ExecutionResultTableRow name='Succeeded' count={succeeded} total={selected} size={succeededBytes} color={succeededColor} onClickCount={() => handleSelectResultType('SUCCEEDED')}/>
            <ExecutionResultTableRow name='Retried' count={retried} size={retriedBytes} />
            <ExecutionResultTableRow name='Remaining' count={remaining} size={remainingBytes} />
            <ExecutionResultTableRow name='Outputs' count={outputs} size={outputBytes} />
            {outputTypes>1?
              <React.Fragment>
                <ExecutionResultTableRow name='Data' count={datas} total={outputs} size={dataBytes} color={noColor}/>
                <ExecutionResultTableRow name='Artifacts' count={artifacts} total={outputs} size={artifactBytes} color={noColor}/>
                <ExecutionResultTableRow name='Samples' count={samples} total={outputs} size={sampleBytes} color={noColor}/>
                <ExecutionResultTableRow name='Diagnostics' count={diagnostics} total={outputs} size={diagnosticBytes} color={noColor}/>
              </React.Fragment>:null}
          </TableBody>
        </Table>
      </TableContainer>
    );
  } else {
    return(
      <div>
        <PieChart style={{ height: '75px' }}
          animate
          lineWidth={35}
          paddingAngle={5}
          lengthAngle={360}
          startAngle={270}
          label={() => percentComplete+'%'}
          labelStyle={{
            fontSize: '20px',
            fontFamily: 'sans-serif',
          }}
          labelPosition={0}
          onClick={(e,index) => handleSelectResultType(resultData[index].title.toUpperCase())}
          data={resultData}
        />
      </div>
    );
  }
}

ExecutionResults.propTypes = {
  value: PropTypes.object.isRequired,
  onSelectResultType: PropTypes.func,
};

ExecutionResults.defaultProps = {
};
